import React from 'react';
import { User, Users, Cloud, Monitor, Clock, Star } from 'lucide-react';
import Tooltip from '../ui/Tooltip';
import UserAvatar from '../ui/UserAvatar';
import { pillColors } from '../../extensions/bundled/task-manager/boardLabels';
import { MAX_LABEL_DOTS } from '../../extensions/bundled/task-manager/boardsOverview';

/**
 * Small pieces the overview tile and the overview row share (cards 7.1/7.4):
 * the Personal / team chip, the Cloud / This device chip, the progress bar,
 * the overdue chip, the label dots, the owner avatar and the pin star.
 * Presentational only.
 */

/** "Personal" with a single-user icon, or the team's name with the users icon. */
export function BoardScopeChip({ isTeam, teamLabel }) {
  const text = isTeam ? teamLabel || 'Team' : 'Personal';
  const label = isTeam ? `Team board: ${text}` : 'Personal board';
  return (
    <Tooltip content={label} side="bottom">
      <span className="boards-tile-chip" role="img" aria-label={label}>
        {isTeam ? <Users size={11} aria-hidden /> : <User size={11} aria-hidden />}
        <span className="boards-tile-chip-text">{text}</span>
      </span>
    </Tooltip>
  );
}

/** "Cloud" = synced; "This device" = kept on this device only. Team boards
 *  are always in the cloud, so they always show Cloud. A grey pill like the
 *  scope chip (minimal tile, 13.10.2026). */
export function BoardLocationChip({ onDevice }) {
  const label = onDevice ? 'On this device only' : 'Synced to cloud';
  return (
    <Tooltip content={label} side="bottom">
      <span className="boards-tile-chip boards-tile-loc" role="img" aria-label={label}>
        {onDevice ? <Monitor size={11} aria-hidden /> : <Cloud size={11} aria-hidden />}
        <span className="boards-tile-chip-text">{onDevice ? 'This device' : 'Cloud'}</span>
      </span>
    </Tooltip>
  );
}

export function BoardProgress({ done, total, percent }) {
  return (
    <span className="boards-progress">
      <span
        className="boards-progress-track"
        role="progressbar"
        aria-label="Tasks done"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
      >
        <span className="boards-progress-fill" style={{ width: `${percent}%` }} />
      </span>
      <span className="boards-progress-count">
        {done}/{total}
      </span>
    </span>
  );
}

export function BoardOverdueChip({ count }) {
  if (!count) return null;
  const label = `${count} overdue ${count === 1 ? 'task' : 'tasks'}`;
  return (
    <span className="boards-overdue" role="img" aria-label={label}>
      <Clock size={10} aria-hidden />
      {count}
    </span>
  );
}

/** Up to five dots in the labels' dot colour, the same one the card's label
 *  pills use (pillColors, list 2). */
export function BoardLabelDots({ colors }) {
  const list = (colors || []).slice(0, MAX_LABEL_DOTS);
  if (list.length === 0) return null;
  return (
    <span className="boards-label-dots" role="img" aria-label={`${list.length} ${list.length === 1 ? 'label' : 'labels'}`}>
      {list.map((key, i) => {
        const { dot } = pillColors(key);
        return <span key={`${key}-${i}`} className="boards-label-dot" style={{ background: dot }} />;
      })}
    </span>
  );
}

/** The pastel fills of the design's member avatars (BoardsGallery / Main
 *  artboards: violet, green, amber …), picked by user id so a person keeps
 *  one colour everywhere. Only the initials fallback shows it: an uploaded
 *  picture covers it. */
const OWNER_PASTELS = ['#c4b5fd', '#6ee7b7', '#fcd34d', '#93c5fd', '#f9a8d4', '#fdba74'];
export function ownerPastel(id) {
  const n = Number(id);
  if (Number.isFinite(n)) return OWNER_PASTELS[Math.abs(Math.trunc(n)) % OWNER_PASTELS.length];
  let h = 0;
  for (const ch of String(id ?? '')) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return OWNER_PASTELS[h % OWNER_PASTELS.length];
}

/** The board owner's 18 px avatar, bottom-right of a tile (extras §2.8). */
export function BoardOwnerAvatar({ owner }) {
  if (!owner) return null;
  const label = owner.name ? `Owner: ${owner.name}` : 'Owner';
  return (
    <Tooltip content={label} side="bottom">
      <span className="boards-tile-owner" role="img" aria-label={label}>
        <UserAvatar
          userId={owner.id}
          name={owner.name}
          size={18}
          style={{ background: ownerPastel(owner.id), color: '#0f0f12', fontSize: 9, fontWeight: 700 }}
        />
      </span>
    </Tooltip>
  );
}

/**
 * The pin star next to the tile's ⋯ (extras §2.2): shown only while the board
 * is pinned. Pinning and unpinning are rows of the ⋯ menu (BoardTileMenu).
 */
export function BoardPinStar({ pinned }) {
  if (!pinned) return null;
  return (
    <span className="boards-tile-pin" role="img" aria-label="Pinned">
      <Star size={12} fill="currentColor" aria-hidden />
    </span>
  );
}
